import React, { useEffect, useMemo, useRef, useState } from 'react';
import LoginModal from '../../components/auth/LoginModal';
import { isAuthenticated as checkIsAuthenticated, getUserMetadata } from '../../utils/supabase';
import { submitDealerCertificate, fetchDealerCertificate, DealerCertificatePayload } from '../../api/dealerCertificateApi';
import { downloadWatchCard } from '../../api/contentApi';

const MAX_TEXT_LENGTH = 80;
const MAX_CUSTOM_LENGTH = 36;

type DialType = '' | 'sunray' | 'mother of pearl' | 'diamond dial';

const Section: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="border border-gray-200 rounded-lg p-5 shadow-sm bg-white">{children}</div>
);

const FormField: React.FC<{
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ label, htmlFor, required, error, children, className = '' }) => (
  <div className={className}>
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
      {label}
      {required ? ' *' : ''}
    </label>
    <div className="mt-1">{children}</div>
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { hasError?: boolean }
>(({ className = '', hasError, ...props }, ref) => (
  <input
    ref={ref}
    className={`block w-full rounded-md border ${
      hasError
        ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
        : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-200'
    } px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 ${className}`.trim()}
    {...props}
  />
));
Input.displayName = 'Input';

const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & { hasError?: boolean }
>(({ className = '', hasError, children, ...props }, ref) => (
  <select
    ref={ref}
    className={`block w-full rounded-md border ${
      hasError
        ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-200'
        : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-200'
    } px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 ${className}`.trim()}
    {...props}
  >
    {children}
  </select>
));
Select.displayName = 'Select';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b pb-2">{children}</h2>
);

const GRID_COL_CLASSES: Record<number, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
};

const GAP_CLASSES: Record<number, string> = {
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
};

const Grid: React.FC<{ cols?: 1 | 2 | 3; gap?: 4 | 5 | 6; children: React.ReactNode; className?: string }> = ({
  cols = 2,
  gap = 5,
  children,
  className = '',
}) => {
  const gridClasses = [
    'grid grid-cols-1',
    GRID_COL_CLASSES[cols] || GRID_COL_CLASSES[2],
    GAP_CLASSES[gap] || GAP_CLASSES[5],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <div className={gridClasses}>{children}</div>;
};

type FormState = {
  serial: string;
  pin: string;
  modelOption: string;
  customModelName: string;
  caseMaterial: string;
  caseFinish: string;
  caseHasStones: 'Yes' | 'No';
  casePreciousMaterial: string;
  customCasePreciousMaterial: string;
  caseCaratWeight: string;
  caseStoneColorOption: string;
  customCaseStoneColor: string;
  movementType: string;
  customMovement: string;
  dialType: DialType;
  sunrayColorOption: string;
  customSunrayColor: string;
  motherOfPearlColor: string;
  customMotherOfPearlColor: string;
  diamondMaterialOption: string;
  customDiamondMaterial: string;
  dialCaratWeight: string;
  dialCut: string;
  dialEmblem: string;
  strapType: string;
  customStrap: string;
  buckleType: string;
  purchaseDate: string;
  warrantyEndDate: string;
  indexGlassPreciousMaterial: string;
  indexGlassCaratWeight: string;
  indexGlassCut: string;
  authorizedRepName: string;
  companyAddress: string;
  companyAddress2: string;
};

type FormErrors = Partial<Record<keyof FormState | 'modelName' | 'dialColor' | 'dialPreciousMaterial' | 'caseStoneColor' | 'movement' | 'strapBracelet', string>>;

const MODEL_OPTIONS = [
  { collection: 'Lucidity', models: ['Gents', 'Lady'] },
  { collection: 'Introsistence', models: ['Gents', 'Oversize', 'Lady', 'Medium', 'Challenge'] },
  { collection: 'Determination', models: ['Gents', 'Medium', 'Lady'] },
  { collection: 'Realization', models: ['Gents', 'Gents Domed'] },
  { collection: 'Connaissance', models: ['Gents'] },
  { collection: "Essence d'esthetique", models: ['S2', 'S6'] },
];

const CASE_MATERIAL_OPTIONS = [
  'stainless steel',
  'rose gold 750',
  'yellow gold 750',
  'white gold 750',
];

const CASE_FINISH_OPTIONS = [
  '',
  'shiny',
  'matte',
  'blackened with diamond-like coating (DLC)',
];

const CASE_STONE_TYPES = [
  'diamond',
  'sapphire',
  'tsavorite',
  'ruby',
];

const CASE_STONE_COLOR_OPTIONS = [
  'white',
  'natural fancy brown',
  'natural fancy yellow',
  'natural fancy cognac',
  'natural fancy pink',
  'pink',
  'orange',
  'blau',
];

const SUNRAY_DIAL_COLORS = [
  'silver',
  'old rose pink',
  'oxide red',
  'chestnut',
  'olive brown',
  'steel greyish blue',
  'light blue',
  'steel medium blue',
  'dark blue',
  'black',
];

const MOTHER_OF_PEARL_COLORS = [
  'bright',
  'dark',
];

const DIAMOND_DIAL_MATERIALS = [
  'diamond white',
  'diamond natural fancy brown',
  'pink sapphire',
  'orange sapphire',
  'sapphire',
  'tsavorite',
  'ruby',
];

const DIAL_EMBLEM_OPTIONS = [
  'without emblem',
  'white gold 750',
  'yellow gold 750',
  'rose gold 750',
];

const MOVEMENT_OPTIONS = [
  { type: 'automatic', options: ['ETA 2824', 'Sellita SW1000'] },
  { type: 'quarz', options: ['Ronda 763'] },
];

const STRAP_OPTIONS = [
  'leather',
  'metal - stainless steel',
  'metal - blackened DLC',
];

const BUCKLE_OPTIONS = [
  'Yellow gold 750',
  'Rose gold 750',
  'White gold 750',
];

const initialFormState: FormState = {
  serial: '',
  pin: '',
  modelOption: '',
  customModelName: '',
  caseMaterial: '',
  caseFinish: '',
  caseHasStones: 'No',
  casePreciousMaterial: '',
  customCasePreciousMaterial: '',
  caseCaratWeight: '',
  caseStoneColorOption: '',
  customCaseStoneColor: '',
  movementType: '',
  customMovement: '',
  dialType: '',
  sunrayColorOption: '',
  customSunrayColor: '',
  motherOfPearlColor: '',
  customMotherOfPearlColor: '',
  diamondMaterialOption: '',
  customDiamondMaterial: '',
  dialCaratWeight: '',
  dialCut: 'Special cut',
  dialEmblem: 'without emblem',
  strapType: '',
  customStrap: '',
  buckleType: '',
  purchaseDate: '',
  warrantyEndDate: '',
  indexGlassPreciousMaterial: '',
  indexGlassCaratWeight: '',
  indexGlassCut: '',
  authorizedRepName: '',
  companyAddress: '',
  companyAddress2: '',
};

const CreateCertificate: React.FC = () => {
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [isFetchingCertificate, setIsFetchingCertificate] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [certificateLoaded, setCertificateLoaded] = useState(false);
  const [validationToast, setValidationToast] = useState<string | null>(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Helper function to convert DD/MM/YYYY to YYYY-MM-DD
  const convertDateFormat = (dateStr: string): string => {
    if (!dateStr) return '';
    // Handle DD/MM/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // If already in YYYY-MM-DD format, return as is
    return dateStr;
  };

  useEffect(() => {
    const verifyAuth = async () => {
      const authed = await checkIsAuthenticated();
      setIsAuthenticated(authed);
      setIsAuthReady(true);
      if (!authed) {
        setShowLoginModal(true);
      }
    };

    verifyAuth();
  }, []);

  // Auto-hide validation toast after 4 seconds
  useEffect(() => {
    if (validationToast) {
      const timer = setTimeout(() => {
        setValidationToast(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [validationToast]);

  // Prevent automatic scrolling on focus
  useEffect(() => {
    let scrollX = 0;
    let scrollY = 0;

    const saveScrollPosition = () => {
      scrollX = window.scrollX;
      scrollY = window.scrollY;
    };

    const restoreScrollPosition = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') {
        setTimeout(() => {
          window.scrollTo(scrollX, scrollY);
        }, 0);
      }
    };

    document.addEventListener('mousedown', saveScrollPosition, true);
    document.addEventListener('focus', restoreScrollPosition, true);

    return () => {
      document.removeEventListener('mousedown', saveScrollPosition, true);
      document.removeEventListener('focus', restoreScrollPosition, true);
    };
  }, []);

  // Fetch user metadata and prepopulate address field
  useEffect(() => {
    const loadUserMetadata = async () => {
      if (isAuthenticated) {
        const { data, error } = await getUserMetadata();
        if (data && data.address && !error) {
          setForm((prev) => ({
            ...prev,
            companyAddress: data.address,
          }));
        }
      }
    };

    loadUserMetadata();
  }, [isAuthenticated]);

  useEffect(() => {
    if (form.caseHasStones === 'No') {
      setForm((current) => ({
        ...current,
        casePreciousMaterial: '',
        customCasePreciousMaterial: '',
        caseCaratWeight: '',
        caseStoneColorOption: '',
        customCaseStoneColor: '',
      }));
    }
  }, [form.caseHasStones]);

  useEffect(() => {
    // Only auto-calculate warranty date if it's not already set
    if (form.purchaseDate && !form.warrantyEndDate) {
      const purchaseDate = new Date(form.purchaseDate);
      const warrantyEndDate = new Date(purchaseDate);
      warrantyEndDate.setFullYear(warrantyEndDate.getFullYear() + 3);
      const warrantyDateString = warrantyEndDate.toISOString().split('T')[0];
      setForm((current) => ({
        ...current,
        warrantyEndDate: warrantyDateString,
      }));
    }
  }, [form.purchaseDate, form.warrantyEndDate]);

  useEffect(() => {
    if (form.dialType === 'diamond dial') {
      setForm((current) => ({
        ...current,
        dialEmblem: 'without emblem',
        sunrayColorOption: '',
        customSunrayColor: '',
        motherOfPearlColor: '',
        customMotherOfPearlColor: '',
      }));
    } else if (form.dialType === 'sunray') {
      setForm((current) => ({
        ...current,
        diamondMaterialOption: '',
        customDiamondMaterial: '',
        dialCaratWeight: '',
        dialCut: '',
        motherOfPearlColor: '',
        customMotherOfPearlColor: '',
      }));
    } else if (form.dialType === 'mother of pearl') {
      setForm((current) => ({
        ...current,
        diamondMaterialOption: '',
        customDiamondMaterial: '',
        dialCaratWeight: '',
        dialCut: '',
        sunrayColorOption: '',
        customSunrayColor: '',
      }));
    } else {
      setForm((current) => ({
        ...current,
        dialEmblem: 'without emblem',
        sunrayColorOption: '',
        customSunrayColor: '',
        motherOfPearlColor: '',
        customMotherOfPearlColor: '',
        diamondMaterialOption: '',
        customDiamondMaterial: '',
        dialCaratWeight: '',
        dialCut: '',
      }));
    }
  }, [form.dialType]);

  const handleInputChange = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error if validation was attempted and field now has value
    if (validationAttempted) {
      // Clear the specific error for this field after a short delay to allow state to update
      setTimeout(() => {
        setErrors((prev) => {
          const newErrors = { ...prev };
          // Remove error for the specific field being edited
          delete newErrors[field];
          // Also check computed fields
          if (field === 'modelOption' || field === 'customModelName') {
            delete newErrors.modelName;
          }
          if (field === 'sunrayColorOption' || field === 'customSunrayColor' || field === 'motherOfPearlColor' || field === 'customMotherOfPearlColor') {
            delete newErrors.dialColor;
          }
          if (field === 'diamondMaterialOption' || field === 'customDiamondMaterial') {
            delete newErrors.dialPreciousMaterial;
          }
          if (field === 'caseStoneColorOption' || field === 'customCaseStoneColor') {
            delete newErrors.caseStoneColor;
          }
          if (field === 'movementType' || field === 'customMovement') {
            delete newErrors.movement;
          }
          if (field === 'strapType' || field === 'customStrap') {
            delete newErrors.strapBracelet;
          }
          return newErrors;
        });
      }, 100);
    }
  };

  const modelName = useMemo(() => {
    return form.modelOption === 'custom' ? form.customModelName.trim() : form.modelOption;
  }, [form.modelOption, form.customModelName]);

  const dialColor = useMemo(() => {
    if (form.dialType === 'sunray') {
      return form.sunrayColorOption === 'custom'
        ? form.customSunrayColor.trim()
        : form.sunrayColorOption;
    }
    if (form.dialType === 'mother of pearl') {
      return form.motherOfPearlColor === 'custom'
        ? form.customMotherOfPearlColor.trim()
        : form.motherOfPearlColor;
    }
    return '';
  }, [form.dialType, form.sunrayColorOption, form.customSunrayColor, form.motherOfPearlColor, form.customMotherOfPearlColor]);

  const dialPreciousMaterial = useMemo(() => {
    if (form.dialType === 'diamond dial') {
      return form.diamondMaterialOption === 'custom'
        ? form.customDiamondMaterial.trim()
        : form.diamondMaterialOption;
    }
    return '';
  }, [form.dialType, form.diamondMaterialOption, form.customDiamondMaterial]);

  const caseStoneColor = useMemo(() => {
    if (form.caseHasStones === 'Yes') {
      return form.caseStoneColorOption === 'custom'
        ? form.customCaseStoneColor.trim()
        : form.caseStoneColorOption;
    }
    return '';
  }, [form.caseHasStones, form.caseStoneColorOption, form.customCaseStoneColor]);

  const movement = useMemo(() => {
    if (form.movementType) {
      return form.customMovement ? form.customMovement.trim() : form.movementType;
    }
    return '';
  }, [form.movementType, form.customMovement]);

  const strapBracelet = useMemo(() => {
    if (form.strapType === 'custom') {
      return form.customStrap.trim();
    }
    return form.strapType;
  }, [form.strapType, form.customStrap]);

  const normalizedPayload: DealerCertificatePayload = {
    serial: form.serial.trim(),
    pin: form.pin.trim(),
    modelName,
    caseMaterial: form.caseMaterial
      ? form.caseFinish
        ? `${form.caseMaterial} (${form.caseFinish})`
        : form.caseMaterial
      : '',
    caseHasStones: form.caseHasStones,
    casePreciousMaterial: form.caseHasStones === 'Yes'
      ? (form.casePreciousMaterial === 'custom' ? form.customCasePreciousMaterial.trim() : form.casePreciousMaterial)
      : '',
    caseCaratWeight: form.caseHasStones === 'Yes' && form.caseCaratWeight ? form.caseCaratWeight : '',
    caseCut: form.caseHasStones === 'Yes' ? caseStoneColor : '',
    movement,
    dialType: form.dialType,
    dialEmblem: form.dialType === 'diamond dial' ? 'without emblem' : form.dialEmblem,
    dialColor: form.dialType === 'sunray' || form.dialType === 'mother of pearl' ? dialColor : '',
    dialPreciousMaterial: form.dialType === 'diamond dial' ? dialPreciousMaterial : '',
    dialCaratWeight: form.dialType === 'diamond dial' ? form.dialCaratWeight : '',
    dialCut: form.dialType === 'diamond dial' ? form.dialCut.trim() : '',
    indexGlassPreciousMaterial: form.indexGlassPreciousMaterial.trim(),
    indexGlassCaratWeight: form.indexGlassCaratWeight,
    indexGlassCut: form.indexGlassCut.trim(),
    strapBracelet,
    buckle: form.buckleType,
    purchaseDate: form.purchaseDate,
    warrantyEndDate: form.warrantyEndDate,
    authorizedRepName: form.authorizedRepName.trim(),
    companyAddress: form.companyAddress.trim(),
    companyAddress2: form.companyAddress2.trim(),
  };

  const validate = (payload: DealerCertificatePayload): FormErrors => {
    const validationErrors: FormErrors = {};
    const trimmedModelName = payload.modelName.trim();
    const trimmedDialColor = payload.dialColor.trim();
    const trimmedDialStone = payload.dialPreciousMaterial.trim();
    const trimmedCaseStone = payload.casePreciousMaterial.trim();
    const trimmedCaseColor = payload.caseCut.trim();

    if (!payload.serial) {
      validationErrors.serial = 'Serial number is required.';
    }
    if (!payload.pin) {
      validationErrors.pin = 'PIN is required.';
    }
    if (!trimmedModelName) {
      validationErrors.modelName = 'Select or enter a model.';
    } else if (trimmedModelName.length > MAX_TEXT_LENGTH) {
      validationErrors.modelName = `Model name must be under ${MAX_TEXT_LENGTH} characters.`;
    }
    if (!form.caseMaterial) {
      validationErrors.caseMaterial = 'Select a case material.';
    }
    if (!payload.movement) {
      validationErrors.movement = 'Movement is required.';
    }
    if (!payload.dialType) {
      validationErrors.dialType = 'Select a dial type.';
    } else {
      if (payload.dialType === 'sunray' || payload.dialType === 'mother of pearl') {
        if (!trimmedDialColor) {
          validationErrors.dialColor = 'Dial color is required.';
        } else if (trimmedDialColor.length > MAX_CUSTOM_LENGTH) {
          validationErrors.dialColor = `Dial color must be under ${MAX_CUSTOM_LENGTH} characters.`;
        }
      }
      if (payload.dialType === 'diamond dial') {
        if (!trimmedDialStone) {
          validationErrors.dialPreciousMaterial = 'Select a dial stone.';
        } else if (trimmedDialStone.length > MAX_CUSTOM_LENGTH) {
          validationErrors.dialPreciousMaterial = `Dial stone must be under ${MAX_CUSTOM_LENGTH} characters.`;
        }
        if (!payload.dialCaratWeight) {
          validationErrors.dialCaratWeight = 'Enter the dial carat weight.';
        }
        if (!payload.dialCut) {
          validationErrors.dialCut = 'Enter the dial cut.';
        }
      }
    }
    if (payload.caseHasStones === 'Yes') {
      if (!trimmedCaseStone) {
        validationErrors.casePreciousMaterial = 'Select a case stone.';
      } else if (trimmedCaseStone.length > MAX_CUSTOM_LENGTH) {
        validationErrors.casePreciousMaterial = `Stone type must be under ${MAX_CUSTOM_LENGTH} characters.`;
      }
      if (payload.caseCaratWeight && Number(payload.caseCaratWeight) < 0) {
        validationErrors.caseCaratWeight = 'Carat weight must be positive.';
      }
      if (!trimmedCaseColor) {
        validationErrors.caseStoneColor = 'Select a stone color.';
      } else if (trimmedCaseColor.length > MAX_CUSTOM_LENGTH) {
        validationErrors.caseStoneColor = `Stone color must be under ${MAX_CUSTOM_LENGTH} characters.`;
      }
    }
    if (!payload.indexGlassPreciousMaterial) {
      validationErrors.indexGlassPreciousMaterial = 'Index glass material is required.';
    }
    if (!payload.strapBracelet) {
      validationErrors.strapBracelet = 'Strap / bracelet details are required.';
    }
    if (!payload.purchaseDate) {
      validationErrors.purchaseDate = 'Purchase date is required.';
    }
    if (!payload.authorizedRepName) {
      validationErrors.authorizedRepName = 'Representative name is required.';
    }
    if (!payload.companyAddress) {
      validationErrors.companyAddress = 'Company address is required.';
    }

    return validationErrors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitMessage(null);
    setSubmitError(null);
    setGenerateError(null);
    setValidationToast(null);
    setValidationAttempted(true);

    const validationErrors = validate(normalizedPayload);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setValidationToast('Please fill all mandatory fields');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);

    try {
      await submitDealerCertificate(normalizedPayload);
      setSubmitMessage('Certificate request saved successfully.');
      setErrors({});
      setValidationAttempted(false);
      setValidationToast(null);
    } catch (error: any) {
      console.error('Failed to submit certificate request:', error);
      const message = error?.message || 'Failed to submit certificate data. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateCertificate = async () => {
    setGenerateError(null);
    setValidationToast(null);
    setValidationAttempted(true);

    const validationErrors = validate(normalizedPayload);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      setValidationToast('Please fill all mandatory fields');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsGenerating(true);

    try {
      const result = await downloadWatchCard(
        normalizedPayload.serial,
        normalizedPayload.pin,
        { certificateData: normalizedPayload }
      );

      if (!result.success || !result.blob) {
        let message = 'Failed to generate certificate PDF.';
        if (result.response) {
          try {
            const errorBody = await result.response.json();
            if (errorBody?.error) {
              message = errorBody.error;
            }
          } catch {
            // Ignore JSON parse errors
          }
        }
        setGenerateError(message);
        return;
      }

      const blobUrl = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = result.filename || `watch-card-${normalizedPayload.serial || 'certificate'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error: any) {
      console.error('Failed to generate certificate PDF:', error);
      const message = error?.message || 'Failed to generate certificate PDF.';
      setGenerateError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFetchCertificate = async () => {
    if (!form.serial || !form.pin) {
      setFetchError('Please enter both serial number and PIN first.');
      return;
    }

    setFetchError(null);
    setIsFetchingCertificate(true);

    try {
      const certificateData = await fetchDealerCertificate(form.serial, form.pin);

      // Map the fetched data to the form state
      const newFormState: FormState = { ...initialFormState };

      // Basic fields
      newFormState.serial = certificateData.serial || form.serial;
      newFormState.pin = certificateData.pin || form.pin;
      newFormState.purchaseDate = convertDateFormat(certificateData.purchaseDate || '');
      newFormState.warrantyEndDate = convertDateFormat(certificateData.warrantyEndDate || '');
      newFormState.authorizedRepName = certificateData.authorizedRepName || '';
      newFormState.companyAddress = certificateData.companyAddress || '';
      newFormState.companyAddress2 = certificateData.companyAddress2 || '';

      // Parse movement
      if (certificateData.movement) {
        const allMovementOptions = MOVEMENT_OPTIONS.flatMap(m => m.options);
        if (allMovementOptions.includes(certificateData.movement)) {
          newFormState.movementType = certificateData.movement;
        } else {
          newFormState.customMovement = certificateData.movement;
        }
      }

      // Parse strap
      if (certificateData.strapBracelet) {
        if (STRAP_OPTIONS.includes(certificateData.strapBracelet)) {
          newFormState.strapType = certificateData.strapBracelet;
        } else {
          newFormState.strapType = 'custom';
          newFormState.customStrap = certificateData.strapBracelet;
        }
      }

      // Parse buckle
      if (certificateData.buckle) {
        newFormState.buckleType = certificateData.buckle;
      }

      // Model name
      if (certificateData.modelName) {
        const allModelOptions = MODEL_OPTIONS.flatMap(group =>
          group.models.map(model => `${group.collection} ${model}`)
        );
        if (allModelOptions.includes(certificateData.modelName)) {
          newFormState.modelOption = certificateData.modelName;
        } else {
          newFormState.modelOption = 'custom';
          newFormState.customModelName = certificateData.modelName;
        }
      }

      // Case material and finish
      if (certificateData.caseMaterial) {
        const caseMaterialMatch = certificateData.caseMaterial.match(/^(.+?)\s*\((.+?)\)$/);
        if (caseMaterialMatch) {
          newFormState.caseMaterial = caseMaterialMatch[1].trim();
          newFormState.caseFinish = caseMaterialMatch[2].trim();
        } else {
          newFormState.caseMaterial = certificateData.caseMaterial;
        }
      }

      // Case stones
      newFormState.caseHasStones = certificateData.caseHasStones === 'Yes' ? 'Yes' : 'No';
      if (newFormState.caseHasStones === 'Yes') {
        if (certificateData.casePreciousMaterial) {
          if (CASE_STONE_TYPES.includes(certificateData.casePreciousMaterial)) {
            newFormState.casePreciousMaterial = certificateData.casePreciousMaterial;
          } else {
            newFormState.casePreciousMaterial = 'custom';
            newFormState.customCasePreciousMaterial = certificateData.casePreciousMaterial;
          }
        }
        newFormState.caseCaratWeight = certificateData.caseCaratWeight || '';
        if (certificateData.caseCut) {
          if (CASE_STONE_COLOR_OPTIONS.includes(certificateData.caseCut)) {
            newFormState.caseStoneColorOption = certificateData.caseCut;
          } else {
            newFormState.caseStoneColorOption = 'custom';
            newFormState.customCaseStoneColor = certificateData.caseCut;
          }
        }
      }

      // Dial type
      newFormState.dialType = certificateData.dialType || '';
      if (certificateData.dialType === 'sunray' || certificateData.dialType === 'mother of pearl') {
        if (certificateData.dialColor) {
          const colorOptions = certificateData.dialType === 'sunray' ? SUNRAY_DIAL_COLORS : MOTHER_OF_PEARL_COLORS;
          if (colorOptions.includes(certificateData.dialColor)) {
            if (certificateData.dialType === 'sunray') {
              newFormState.sunrayColorOption = certificateData.dialColor;
            } else {
              newFormState.motherOfPearlColor = certificateData.dialColor;
            }
          } else {
            if (certificateData.dialType === 'sunray') {
              newFormState.sunrayColorOption = 'custom';
              newFormState.customSunrayColor = certificateData.dialColor;
            } else {
              newFormState.motherOfPearlColor = 'custom';
              newFormState.customMotherOfPearlColor = certificateData.dialColor;
            }
          }
        }
        newFormState.dialEmblem = certificateData.dialEmblem || 'without emblem';
      } else if (certificateData.dialType === 'diamond dial') {
        if (certificateData.dialPreciousMaterial) {
          if (DIAMOND_DIAL_MATERIALS.includes(certificateData.dialPreciousMaterial)) {
            newFormState.diamondMaterialOption = certificateData.dialPreciousMaterial;
          } else {
            newFormState.diamondMaterialOption = 'custom';
            newFormState.customDiamondMaterial = certificateData.dialPreciousMaterial;
          }
        }
        newFormState.dialCaratWeight = certificateData.dialCaratWeight || '';
        newFormState.dialCut = certificateData.dialCut || '';
      }

      // Index glass
      newFormState.indexGlassPreciousMaterial = certificateData.indexGlassPreciousMaterial || '';
      newFormState.indexGlassCaratWeight = certificateData.indexGlassCaratWeight || '';
      newFormState.indexGlassCut = certificateData.indexGlassCut || '';

      setForm(newFormState);
      setErrors({});
      setValidationAttempted(false);
      setCertificateLoaded(true);
    } catch (error: any) {
      console.error('Failed to fetch certificate:', error);
      const message = error?.message || 'Failed to fetch certificate data.';
      setFetchError(message);
      setCertificateLoaded(false);
    } finally {
      setIsFetchingCertificate(false);
    }
  };

  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Checking access...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-100 pb-16">
      <div className="sticky top-0 left-0 right-0 z-20 bg-gray-100/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">Create Watch Certificate</h1>
            <p className="text-sm text-gray-600 mt-1">
              {certificateLoaded
                ? 'Fill out the details below to add a new certificate entry. Fields marked with * are required.'
                : 'Enter serial number and PIN to load certificate details from the spreadsheet.'}
            </p>
          </div>
          {isAuthenticated && certificateLoaded && (
            <div className="flex flex-shrink-0 gap-3">
              <button
                type="button"
                onClick={handleGenerateCertificate}
                disabled={isGenerating || isSubmitting}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {isGenerating ? 'Generating...' : 'Preview'}
              </button>
              <button
                type="button"
                onClick={() => formRef.current?.requestSubmit()}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">

        {isAuthenticated ? (
          <>
            {validationToast && (
              <div className="mb-4 rounded-md bg-yellow-50 p-4 border border-yellow-200 shadow-md">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-yellow-600 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium text-yellow-800">{validationToast}</p>
                </div>
              </div>
            )}
            {submitMessage && (
              <div className="mb-4 rounded-md bg-green-50 p-4 border border-green-200">
                <p className="text-sm font-medium text-green-800">{submitMessage}</p>
              </div>
            )}
            {submitError && (
              <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
                <p className="text-sm font-medium text-red-800">{submitError}</p>
              </div>
            )}
            {generateError && (
              <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
                <p className="text-sm font-medium text-red-800">{generateError}</p>
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
              <Section>
                <div className="flex items-start justify-between mb-4 border-b pb-2">
                  <SectionTitle>Watch Identification</SectionTitle>
                  <div className="flex gap-2">
                    {!certificateLoaded && (
                      <button
                        type="button"
                        onClick={handleFetchCertificate}
                        disabled={isFetchingCertificate || !form.serial || !form.pin}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Fetch certificate details from spreadsheet"
                      >
                        {isFetchingCertificate ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Load Certificate
                          </>
                        )}
                      </button>
                    )}
                    {certificateLoaded && (
                      <button
                        type="button"
                        onClick={() => {
                          setForm(initialFormState);
                          setCertificateLoaded(false);
                          setFetchError(null);
                          setErrors({});
                          setValidationAttempted(false);
                          setValidationToast(null);
                        }}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Start over with a different certificate"
                      >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reset
                      </button>
                    )}
                  </div>
                </div>
                {fetchError && (
                  <div className="mb-4 rounded-md bg-red-50 p-3 border border-red-200">
                    <p className="text-sm font-medium text-red-800">{fetchError}</p>
                  </div>
                )}
                <Grid>
                  <FormField label="Serial Number" htmlFor="serial" required error={errors.serial}>
                    <Input
                      id="serial"
                      type="text"
                      value={form.serial}
                      onChange={handleInputChange('serial')}
                      maxLength={MAX_TEXT_LENGTH}
                      required
                      disabled={certificateLoaded}
                      hasError={!!errors.serial}
                    />
                  </FormField>
                  <FormField label="PIN" htmlFor="pin" required error={errors.pin}>
                    <Input
                      id="pin"
                      type="text"
                      value={form.pin}
                      onChange={handleInputChange('pin')}
                      maxLength={MAX_TEXT_LENGTH}
                      required
                      disabled={certificateLoaded}
                      hasError={!!errors.pin}
                    />
                  </FormField>
                </Grid>
                {certificateLoaded && <div className="mt-6">
                  <FormField label="Model" htmlFor="modelOption" required error={errors.modelName}>
                    <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                      <Select
                        id="modelOption"
                        value={form.modelOption}
                        onChange={(event) => {
                          const { value } = event.target;
                          setForm((prev) => ({
                            ...prev,
                            modelOption: value,
                            customModelName: value === 'custom' ? prev.customModelName : '',
                          }));
                          if (validationAttempted) {
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.modelName;
                              return newErrors;
                            });
                          }
                        }}
                        hasError={!!errors.modelName}
                      >
                        <option value="">Select model</option>
                        {MODEL_OPTIONS.map((group) => (
                          <optgroup key={group.collection} label={group.collection}>
                            {group.models.map((model) => {
                              const fullName = `${group.collection} ${model}`;
                              return (
                                <option key={fullName} value={fullName}>
                                  {fullName}
                                </option>
                              );
                            })}
                          </optgroup>
                        ))}
                        <option value="custom">Custom</option>
                      </Select>
                      {form.modelOption === 'custom' && (
                        <Input
                          type="text"
                          value={form.customModelName}
                          onChange={handleInputChange('customModelName')}
                          maxLength={MAX_TEXT_LENGTH}
                          placeholder="Enter model name"
                          hasError={!!errors.modelName}
                        />
                      )}
                    </div>
                  </FormField>
                </div>}
              </Section>

              {certificateLoaded && (
                <>
              <Section>
                <SectionTitle>Case</SectionTitle>
                <Grid>
                  <FormField label="Case Material" htmlFor="caseMaterial" required error={errors.caseMaterial}>
                    <Select
                      id="caseMaterial"
                      value={form.caseMaterial}
                      onChange={handleInputChange('caseMaterial')}
                      hasError={!!errors.caseMaterial}
                    >
                      <option value="">Select material</option>
                      {CASE_MATERIAL_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </Select>
                  </FormField>
                  <FormField label="Case Finish" htmlFor="caseFinish">
                    <Select
                      id="caseFinish"
                      value={form.caseFinish}
                      onChange={handleInputChange('caseFinish')}
                    >
                      {CASE_FINISH_OPTIONS.map((option) => (
                        <option key={option || 'none'} value={option}>{option || '—'}</option>
                      ))}
                    </Select>
                  </FormField>
                </Grid>
                <Grid className="mt-6">
                  <FormField label="Stones on Case" htmlFor="caseHasStones">
                    <Select
                      id="caseHasStones"
                      value={form.caseHasStones}
                      onChange={(event) => {
                        const value = event.target.value as 'Yes' | 'No';
                        setForm((prev) => ({
                          ...prev,
                          caseHasStones: value,
                        }));
                      }}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </Select>
                  </FormField>
                  {form.caseHasStones === 'Yes' && (
                    <FormField label="Stone Type" htmlFor="casePreciousMaterial" required error={errors.casePreciousMaterial}>
                      <Select
                        id="casePreciousMaterial"
                        value={form.casePreciousMaterial}
                        onChange={(event) => {
                          const { value } = event.target;
                          setForm((prev) => ({
                            ...prev,
                            casePreciousMaterial: value,
                            customCasePreciousMaterial: value === 'custom' ? prev.customCasePreciousMaterial : '',
                          }));
                          if (validationAttempted) {
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.casePreciousMaterial;
                              return newErrors;
                            });
                          }
                        }}
                        hasError={!!errors.casePreciousMaterial}
                      >
                        <option value="">Select stone</option>
                        {CASE_STONE_TYPES.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                        <option value="custom">Custom</option>
                      </Select>
                      {form.casePreciousMaterial === 'custom' && (
                        <Input
                          type="text"
                          value={form.customCasePreciousMaterial}
                          onChange={handleInputChange('customCasePreciousMaterial')}
                          maxLength={MAX_CUSTOM_LENGTH}
                          placeholder="Enter custom stone"
                          className="mt-2"
                          hasError={!!errors.casePreciousMaterial}
                        />
                      )}
                    </FormField>
                  )}
                </Grid>
                {form.caseHasStones === 'Yes' && (
                  <Grid cols={3} className="mt-6">
                    <FormField label="Total Carat Weight" htmlFor="caseCaratWeight" error={errors.caseCaratWeight}>
                      <Input
                        id="caseCaratWeight"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.caseCaratWeight}
                        onChange={handleInputChange('caseCaratWeight')}
                      />
                    </FormField>
                    <FormField label="Stone Color" htmlFor="caseStoneColorOption" required error={errors.caseStoneColor} className="md:col-span-2">
                      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                        <Select
                          id="caseStoneColorOption"
                          value={form.caseStoneColorOption}
                          onChange={(event) => {
                            const { value } = event.target;
                            setForm((prev) => ({
                              ...prev,
                              caseStoneColorOption: value,
                              customCaseStoneColor: value === 'custom' ? prev.customCaseStoneColor : '',
                            }));
                            if (validationAttempted) {
                              setErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.caseStoneColor;
                                return newErrors;
                              });
                            }
                          }}
                          hasError={!!errors.caseStoneColor}
                        >
                          <option value="">Select color</option>
                          {CASE_STONE_COLOR_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                          <option value="custom">Custom</option>
                        </Select>
                        {form.caseStoneColorOption === 'custom' && (
                          <Input
                            type="text"
                            value={form.customCaseStoneColor}
                            onChange={handleInputChange('customCaseStoneColor')}
                            maxLength={MAX_CUSTOM_LENGTH}
                            placeholder="Enter color"
                            hasError={!!errors.caseStoneColor}
                          />
                        )}
                      </div>
                    </FormField>
                  </Grid>
                )}
              </Section>

              <Section>
                <SectionTitle>Dial</SectionTitle>
                <FormField label="Dial Type" htmlFor="dialType" required error={errors.dialType}>
                  <Select
                    id="dialType"
                    value={form.dialType}
                    onChange={(event) => {
                      const value = event.target.value as DialType;
                      setForm((prev) => ({
                        ...prev,
                        dialType: value,
                      }));
                      if (validationAttempted) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.dialType;
                          return newErrors;
                        });
                      }
                    }}
                    required
                    hasError={!!errors.dialType}
                  >
                    <option value="">Select dial type</option>
                    <option value="sunray">Sunray</option>
                    <option value="mother of pearl">Mother of Pearl</option>
                    <option value="diamond dial">Diamond Dial</option>
                  </Select>
                </FormField>

                {(form.dialType === 'sunray' || form.dialType === 'mother of pearl') && (
                  <Grid className="mt-6">
                    <FormField label="Dial Color" htmlFor="dialColor" required error={errors.dialColor}>
                      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                        {form.dialType === 'sunray' ? (
                          <>
                            <Select
                              value={form.sunrayColorOption}
                              onChange={(event) => {
                                const { value } = event.target;
                                setForm((prev) => ({
                                  ...prev,
                                  sunrayColorOption: value,
                                  customSunrayColor: value === 'custom' ? prev.customSunrayColor : '',
                                }));
                                if (validationAttempted) {
                                  setErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors.dialColor;
                                    return newErrors;
                                  });
                                }
                              }}
                              hasError={!!errors.dialColor}
                            >
                              <option value="">Select color</option>
                              {SUNRAY_DIAL_COLORS.map((color) => (
                                <option key={color} value={color}>{color}</option>
                              ))}
                              <option value="custom">Custom</option>
                            </Select>
                            {form.sunrayColorOption === 'custom' && (
                              <Input
                                type="text"
                                value={form.customSunrayColor}
                                onChange={handleInputChange('customSunrayColor')}
                                maxLength={MAX_CUSTOM_LENGTH}
                                placeholder="Enter color"
                                hasError={!!errors.dialColor}
                              />
                            )}
                          </>
                        ) : (
                          <>
                            <Select
                              value={form.motherOfPearlColor}
                              onChange={(event) => {
                                const { value } = event.target;
                                setForm((prev) => ({
                                  ...prev,
                                  motherOfPearlColor: value,
                                  customMotherOfPearlColor: value === 'custom' ? prev.customMotherOfPearlColor : '',
                                }));
                                if (validationAttempted) {
                                  setErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors.dialColor;
                                    return newErrors;
                                  });
                                }
                              }}
                              hasError={!!errors.dialColor}
                            >
                              <option value="">Select color</option>
                              {MOTHER_OF_PEARL_COLORS.map((color) => (
                                <option key={color} value={color}>{color}</option>
                              ))}
                              <option value="custom">Custom</option>
                            </Select>
                            {form.motherOfPearlColor === 'custom' && (
                              <Input
                                type="text"
                                value={form.customMotherOfPearlColor}
                                onChange={handleInputChange('customMotherOfPearlColor')}
                                maxLength={MAX_CUSTOM_LENGTH}
                                placeholder="Enter color"
                                hasError={!!errors.dialColor}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </FormField>
                    <FormField label="Dial Emblem" htmlFor="dialEmblem">
                      <Select
                        value={form.dialEmblem}
                        onChange={handleInputChange('dialEmblem')}
                        className="disabled:bg-gray-100"
                      >
                        {DIAL_EMBLEM_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </Select>
                      <p className="mt-1 text-xs text-gray-500">
                        Only sunray and mother of pearl dials can feature an emblem.
                      </p>
                    </FormField>
                  </Grid>
                )}

                {form.dialType === 'diamond dial' && (
                  <Grid cols={3} className="mt-6">
                    <FormField label="Dial Stone" htmlFor="dialStone" required error={errors.dialPreciousMaterial} className="md:col-span-2">
                      <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                        <Select
                          value={form.diamondMaterialOption}
                          onChange={(event) => {
                            const { value } = event.target;
                            setForm((prev) => ({
                              ...prev,
                              diamondMaterialOption: value,
                              customDiamondMaterial: value === 'custom' ? prev.customDiamondMaterial : '',
                            }));
                            if (validationAttempted) {
                              setErrors((prev) => {
                                const newErrors = { ...prev };
                                delete newErrors.dialPreciousMaterial;
                                return newErrors;
                              });
                            }
                          }}
                          hasError={!!errors.dialPreciousMaterial}
                        >
                          <option value="">Select stone</option>
                          {DIAMOND_DIAL_MATERIALS.map((stone) => (
                            <option key={stone} value={stone}>{stone}</option>
                          ))}
                          <option value="custom">Custom</option>
                        </Select>
                        {form.diamondMaterialOption === 'custom' && (
                          <Input
                            type="text"
                            value={form.customDiamondMaterial}
                            onChange={handleInputChange('customDiamondMaterial')}
                            maxLength={MAX_CUSTOM_LENGTH}
                            placeholder="Enter stone"
                            hasError={!!errors.dialPreciousMaterial}
                          />
                        )}
                      </div>
                    </FormField>
                    <FormField label="Dial Carat Weight" htmlFor="dialCaratWeight" required error={errors.dialCaratWeight}>
                      <Input
                        id="dialCaratWeight"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.dialCaratWeight}
                        onChange={handleInputChange('dialCaratWeight')}
                        hasError={!!errors.dialCaratWeight}
                      />
                    </FormField>
                    <FormField label="Dial Cut" htmlFor="dialCut" required error={errors.dialCut} className="md:col-span-3">
                      <Input
                        id="dialCut"
                        type="text"
                        value={form.dialCut}
                        onChange={handleInputChange('dialCut')}
                        maxLength={MAX_CUSTOM_LENGTH}
                        hasError={!!errors.dialCut}
                      />
                    </FormField>
                  </Grid>
                )}
              </Section>

              <Section>
                <SectionTitle>Movement</SectionTitle>
                <FormField label="Movement Type" htmlFor="movementType" required error={errors.movement}>
                  <Select
                    id="movementType"
                    value={form.customMovement || form.movementType}
                    onChange={(event) => {
                      const value = event.target.value;
                      const isCustom = !MOVEMENT_OPTIONS.some(m =>
                        m.type === value || m.options.includes(value)
                      );
                      setForm((prev) => ({
                        ...prev,
                        movementType: isCustom ? '' : value,
                        customMovement: isCustom ? value : '',
                      }));
                      if (validationAttempted) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.movement;
                          return newErrors;
                        });
                      }
                    }}
                    hasError={!!errors.movement}
                  >
                    <option value="">Select movement</option>
                    {MOVEMENT_OPTIONS.map((movement) => (
                      <optgroup key={movement.type} label={movement.type}>
                        {movement.options.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </FormField>
              </Section>

              <Section>
                <SectionTitle>Strap & Buckles</SectionTitle>
                <Grid>
                  <FormField label="Strap Type" htmlFor="strapType" required error={errors.strapBracelet}>
                    <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                      <Select
                        id="strapType"
                        value={form.strapType}
                        onChange={(event) => {
                          const { value } = event.target;
                          setForm((prev) => ({
                            ...prev,
                            strapType: value,
                            customStrap: value === 'custom' ? prev.customStrap : '',
                          }));
                          if (validationAttempted) {
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.strapBracelet;
                              return newErrors;
                            });
                          }
                        }}
                        hasError={!!errors.strapBracelet}
                      >
                        <option value="">Select strap</option>
                        {STRAP_OPTIONS.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                        <option value="custom">Custom</option>
                      </Select>
                      {form.strapType === 'custom' && (
                        <Input
                          type="text"
                          value={form.customStrap}
                          onChange={handleInputChange('customStrap')}
                          maxLength={MAX_TEXT_LENGTH}
                          placeholder="Enter strap type"
                          hasError={!!errors.strapBracelet}
                        />
                      )}
                    </div>
                  </FormField>
                  <FormField label="Buckle" htmlFor="buckleType" error={errors.buckleType}>
                    <Select
                      id="buckleType"
                      value={form.buckleType}
                      onChange={handleInputChange('buckleType')}
                    >
                      <option value="">Select buckle (optional)</option>
                      {BUCKLE_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </Select>
                  </FormField>
                </Grid>
              </Section>

              <Section>
                <SectionTitle>Index Glass</SectionTitle>
                <Grid cols={3} className="mt-6">
                  <FormField label="Index Glass Material" htmlFor="indexGlassPreciousMaterial" required error={errors.indexGlassPreciousMaterial}>
                    <Input
                      id="indexGlassPreciousMaterial"
                      type="text"
                      value={form.indexGlassPreciousMaterial}
                      onChange={handleInputChange('indexGlassPreciousMaterial')}
                      maxLength={MAX_TEXT_LENGTH}
                      hasError={!!errors.indexGlassPreciousMaterial}
                    />
                  </FormField>
                  <FormField label="Index Carat Weight" htmlFor="indexGlassCaratWeight">
                    <Input
                      id="indexGlassCaratWeight"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.indexGlassCaratWeight}
                      onChange={handleInputChange('indexGlassCaratWeight')}
                    />
                  </FormField>
                  <FormField label="Index Cut" htmlFor="indexGlassCut">
                    <Input
                      id="indexGlassCut"
                      type="text"
                      value={form.indexGlassCut}
                      onChange={handleInputChange('indexGlassCut')}
                      maxLength={MAX_CUSTOM_LENGTH}
                    />
                  </FormField>
                </Grid>
              </Section>

              <Section>
                <SectionTitle>Purchase Details</SectionTitle>
                <Grid>
                  <FormField label="Purchase Date" htmlFor="purchaseDate" required error={errors.purchaseDate}>
                    <Input
                      id="purchaseDate"
                      type="date"
                      value={form.purchaseDate}
                      onChange={handleInputChange('purchaseDate')}
                      hasError={!!errors.purchaseDate}
                    />
                  </FormField>
                  <FormField label="Warranty End Date" htmlFor="warrantyEndDate">
                    <Input
                      id="warrantyEndDate"
                      type="date"
                      value={form.warrantyEndDate}
                      onChange={handleInputChange('warrantyEndDate')}
                    />
                  </FormField>
                </Grid>
              </Section>

              <Section>
                <SectionTitle>Dealer Information</SectionTitle>
                <Grid>
                  <FormField label="Authorized Representative" htmlFor="authorizedRepName" required error={errors.authorizedRepName}>
                    <Input
                      id="authorizedRepName"
                      type="text"
                      value={form.authorizedRepName}
                      onChange={handleInputChange('authorizedRepName')}
                      maxLength={MAX_TEXT_LENGTH}
                      hasError={!!errors.authorizedRepName}
                    />
                  </FormField>
                  <FormField label="Company Address Line 1" htmlFor="companyAddress" required error={errors.companyAddress}>
                    <Input
                      id="companyAddress"
                      type="text"
                      value={form.companyAddress}
                      onChange={handleInputChange('companyAddress')}
                      maxLength={MAX_TEXT_LENGTH}
                      hasError={!!errors.companyAddress}
                    />
                  </FormField>
                </Grid>
                <div className="mt-6">
                  <FormField label="Company Address Line 2" htmlFor="companyAddress2">
                    <Input
                      id="companyAddress2"
                      type="text"
                      value={form.companyAddress2}
                      onChange={handleInputChange('companyAddress2')}
                      maxLength={MAX_TEXT_LENGTH}
                    />
                  </FormField>
                </div>
              </Section>

              </>
              )}
            </form>
          </>
        ) : (
          <div className="py-24 text-center">
            <p className="mb-4 text-lg text-gray-600">Dealer login required to create certificates.</p>
            <button
              type="button"
              onClick={() => setShowLoginModal(true)}
              className="inline-flex items-center rounded-md border border-transparent bg-gray-800 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800"
            >
              Sign In
            </button>
          </div>
        )}
      </div>

      {!isAuthenticated && showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => {
            setIsAuthenticated(true);
            setShowLoginModal(false);
          }}
          title="Dealer Access"
          description="Please sign in to continue."
          submitLabel="Continue"
        />
      )}
    </div>
  );
};

export default CreateCertificate;
